import { useEffect, useState, useSyncExternalStore } from 'react';

type Theme = 'light' | 'dark';

function readDarkModeFromDom(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

function subscribeToDarkMode(onStoreChange: () => void): () => void {
  const observer = new MutationObserver(onStoreChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
  return () => observer.disconnect();
}

export function useIsDarkMode(): boolean {
  return useSyncExternalStore(subscribeToDarkMode, readDarkModeFromDom, () => false);
}

export function useDarkMode() {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first, fallback to system preference
    const stored = localStorage.getItem('theme') as Theme;
    if (stored && (stored === 'light' || stored === 'dark')) {
      return stored;
    }
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(theme);

  useEffect(() => {
    const root = window.document.documentElement;
    
    const updateTheme = () => {
      setResolvedTheme(theme);
      
      // Apply theme to DOM
      root.classList.remove('light', 'dark');
      root.classList.add(theme);
    };

    // Initial update
    updateTheme();

    // Listen for system theme changes (only when no user preference is set)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Only update if no user preference is stored
      const stored = localStorage.getItem('theme');
      if (!stored) {
        const systemTheme = mediaQuery.matches ? 'dark' : 'light';
        setTheme(systemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    // Save to localStorage
    localStorage.setItem('theme', theme);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  const setThemeMode = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  return {
    theme,
    resolvedTheme,
    setTheme: setThemeMode,
  };
}

